import { useCallback, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  useCreateSample,
  useCreateConcatenatedFastqgz,
  useTriggerAnalysis,
  useCreateSequencingRun,
  useUpdateSample,
  useUpdateSequencingRun
} from '../services/api/KeynomeAPIMutations';
import ApiMethods from '../services/api/ApiMethods';
import {
  sortByPod5Index
} from './minKNOWDirectoryUtils';
import { fetchSampleAnalysisStatus } from '../services/api/analysisUtils';
import {
  buildError,
  buildGouramiDatasetAttributes,
  shouldSkipAnalysisStatus,
  validateDirectoryStructure
} from './gouramiUploadUtils';

const STATUS = {
  validating: { detail: 'Validating files...' },
  creatingDataset: { detail: 'Preparing run...' },
  uploading: { detail: 'Preparing run...' },
  finalizing: { detail: 'Finishing up...' },
  complete: { detail: 'Upload complete' }
};

const UPLOAD_TOTAL_WEIGHT = 85;
const WEIGHT_CREATE_SAMPLE = 0.25;
const WEIGHT_CREATE_FASTQ = 0.25;
const WEIGHT_UPLOAD = 0.4;
const WEIGHT_TRIGGER = 0.1;
const API_ERROR_CODES = {
  datasetLookup: 'DATASET_LOOKUP_ERROR',
  existingSamplesFetch: 'EXISTING_SAMPLES_FETCH_ERROR',
  sampleStatusFetch: 'SAMPLE_STATUS_FETCH_ERROR',
  datasetUpdate: 'DATASET_UPDATE_ERROR',
  sampleUpdate: 'SAMPLE_UPDATE_ERROR'
};

async function findExistingDataset({ projectId, datasetName, accessToken }) {
  try {
    const runs = await ApiMethods.get(
      `v1/projects/${projectId}/sequencing_runs`,
      accessToken
    );
    return runs?.find?.((run) => run.attributes?.name === datasetName) || null;
  } catch (error) {
    throw buildError(API_ERROR_CODES.datasetLookup, 'Failed to fetch sequencing datasets.', { cause: error });
  }
}

async function fetchExistingSamples({ datasetId, accessToken }) {
  try {
    const samples = await ApiMethods.get(
      `v1/sequencing_runs/${datasetId}/lib_seqs`,
      accessToken
    );

    return new Map(
      (samples || [])
        .filter((sample) => sample?.attributes?.sample_name)
        .map((sample) => [sample.attributes.sample_name, sample])
    );
  } catch (error) {
    throw buildError(API_ERROR_CODES.existingSamplesFetch, 'Failed to fetch existing samples.', { cause: error });
  }
}

async function processSampleEntry({
  entry,
  datasetId,
  perSampleWeight,
  existingSampleId = null,
  mutations,
  advance,
  setStatus,
  samplePosition,
  uploadEstimate
}) {
  const entryDisplayName = samplePosition?.total > 1
    ? `${entry?.name} (${samplePosition.index + 1} of ${samplePosition.total})`
    : entry?.name;

  setStatus?.({ detail: `Preparing ${entryDisplayName}...` });
  const createSampleWeight = perSampleWeight * WEIGHT_CREATE_SAMPLE;
  const createFastqWeight = perSampleWeight * WEIGHT_CREATE_FASTQ;
  const uploadWeight = perSampleWeight * WEIGHT_UPLOAD;
  const triggerWeight = perSampleWeight * WEIGHT_TRIGGER;

  const { createSample, createConcatenatedFastqgz, triggerAnalysis } = mutations;
  
  setStatus?.({ detail: `Preparing ${entryDisplayName}...` });
  const sortedFiles = sortByPod5Index(entry.files);

  setStatus?.({ detail: `Preparing ${entryDisplayName}...` });
  const concatenatedBlob = new Blob(sortedFiles, { type: 'application/gzip' });

  let sample = existingSampleId ? { id: existingSampleId } : null;
  if (!sample) {
    try {
      setStatus?.({ detail: `Preparing ${entryDisplayName}...` });
      sample = await createSample({
        runId: datasetId,
        sampleType: 'isolate',
        sampleName: entry.name,
        file: sortedFiles[0]
      });
    } catch (error) {
      throw buildError('API_ERROR', 'Failed to create sample.', { cause: error, stage: 'createSample', entryName: entry?.name });
    }
  } else {
    setStatus?.({ detail: `Preparing ${entryDisplayName}...` });
  }
  advance(createSampleWeight);

  let fastq;
  try {
    setStatus?.({ detail: `Preparing ${entryDisplayName}...` });
    fastq = await createConcatenatedFastqgz({
      libSeqId: sample.id,
      queryParams: { extraFields: ['location_signed_upload_url'] }
    });
  } catch (error) {
    throw buildError('API_ERROR', 'Failed to create FASTQ record.', { cause: error, stage: 'createFastqRecord', entryName: entry?.name });
  }
  advance(createFastqWeight);

  try {
    setStatus?.({ detail: `Uploading ${entryDisplayName}...` });
    uploadEstimate?.onSampleUploadStart?.(entry, concatenatedBlob.size);
    let previouslyAdvanced = 0;
    let uploadBytesSent = false;
    await ApiMethods.uploadToSignedUrl(
      fastq.attributes.location_signed_upload_url,
      concatenatedBlob,
      'application/gzip',
      ({ loaded, total }) => {
        if (!total) {
          return;
        }

        // Convert bytes-uploaded ratio into the upload step's share of overall
        // progress, then advance only the incremental difference so the
        // end-to-end progress bar moves proportionally to actual PUT transfer.
        const uploadRatio = Math.min(1, loaded / total);
        uploadEstimate?.onSampleUploadProgress?.(loaded, total);
        if (!uploadBytesSent && uploadRatio >= 1) {
          uploadBytesSent = true;
          setStatus?.({ detail: `Finishing ${entryDisplayName}...` });
        }

        const targetAdvanced = uploadWeight * uploadRatio;
        const delta = targetAdvanced - previouslyAdvanced;
        if (delta <= 0) {
          return;
        }

        previouslyAdvanced = targetAdvanced;
        advance(delta);
      }
    );

    // Flush any weight not yet reported — covers rounding gaps and the case
    // where no onProgress events fired (e.g. very small / instant uploads).
    const remainingUploadWeight = uploadWeight - previouslyAdvanced;
    if (remainingUploadWeight > 0) {
      advance(remainingUploadWeight);
    }
    uploadEstimate?.onSampleUploadComplete?.(concatenatedBlob.size);
  } catch (error) {
    uploadEstimate?.onSampleUploadError?.();
    throw buildError('UPLOAD_ERROR', 'Failed to upload sequencing data.', { cause: error, stage: 'upload', entryName: entry?.name });
  }

  try {
    setStatus?.({ detail: `Preparing ${entryDisplayName}...` });
    await triggerAnalysis({ fastqId: fastq.id });
  } catch (error) {
    throw buildError('API_ERROR', 'Failed to trigger analysis.', { cause: error, stage: 'triggerAnalysis', entryName: entry?.name });
  }
  advance(triggerWeight);
}

export function useMinKNOWUpload({ projectId, onSuccess, onError }) {
  const { getAccessTokenSilently } = useAuth0();
  const { mutateAsync: createSample } = useCreateSample();
  const { mutateAsync: createConcatenatedFastqgz } = useCreateConcatenatedFastqgz();
  const { mutateAsync: triggerAnalysis } = useTriggerAnalysis();
  const { mutateAsync: createSequencingRun } = useCreateSequencingRun();
  const { mutateAsync: updateSample } = useUpdateSample();
  const { mutateAsync: updateSequencingRun } = useUpdateSequencingRun();

  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState({ detail: '' });
  const [isUploading, setIsUploading] = useState(false);
  const [uploadEstimate, setUploadEstimate] = useState(null);

  const upload = useCallback(async (files = []) => {
    if (!projectId) {
      onError?.(buildError('PROJECT_REQUIRED', 'Select a project before uploading.'));
      return;
    }

    setIsUploading(true);
    setUploadEstimate(null);
    setStatus(STATUS.validating);
    setProgress(0);

    let progressValue = 0;
    const setProgressValue = (value) => {
      progressValue = Math.min(100, value);
      setProgress(Math.round(progressValue));
    };
    const advance = (delta) => setProgressValue(progressValue + delta);

    try {
      const { metadata, sampleEntries } = await validateDirectoryStructure(files);

      const accessToken = await getAccessTokenSilently();

      const existingDataset = await findExistingDataset({
        projectId,
        datasetName: metadata.datasetName,
        accessToken
      });

      setProgressValue(5);
      setStatus(STATUS.creatingDataset);

      let dataset;
      let existingSamplesByName = new Map();
      const datasetAttributes = buildGouramiDatasetAttributes(metadata);

      if (existingDataset) {
        try {
          dataset = await updateSequencingRun({
            datasetId: existingDataset.id,
            attributes: datasetAttributes
          });
        } catch (error) {
          throw buildError(API_ERROR_CODES.datasetUpdate, 'Failed to update sequencing dataset.', { cause: error });
        }
        existingSamplesByName = await fetchExistingSamples({
          datasetId: existingDataset.id,
          accessToken
        });
      } else {
        try {
          dataset = await createSequencingRun({
            projectId,
            attributes: {
              name: metadata.datasetName,
              ...datasetAttributes
            }
          });
        } catch (error) {
          throw buildError('API_ERROR', 'Failed to create sequencing dataset.', { cause: error });
        }
      }

      setProgressValue(15);
      setStatus(STATUS.uploading);

      const entriesToProcess = [];
      for (const entry of sampleEntries) {
        const existingSample = existingSamplesByName.get(entry.name);
        if (!existingSample?.id) {
          entriesToProcess.push({ entry, existingSampleId: null });
          continue;
        }

        let analysisStatus = null;
        try {
          analysisStatus = await fetchSampleAnalysisStatus({
            sampleId: existingSample.id,
            accessToken
          });
        } catch (error) {
          throw buildError(API_ERROR_CODES.sampleStatusFetch, 'Failed to fetch sample analysis status.', { cause: error });
        }
        if (shouldSkipAnalysisStatus(analysisStatus)) {
          setStatus({ label: `Skipping ${entry?.name}`, detail: 'already processed.' });
          continue;
        }

        try {
          await updateSample({
            sampleId: existingSample.id,
            sampleName: entry.name,
            sampleType: 'isolate'
          });
        } catch (error) {
          throw buildError(API_ERROR_CODES.sampleUpdate, 'Failed to update sample.', { cause: error, stage: 'updateSample', entryName: entry?.name });
        }

        entriesToProcess.push({ entry, existingSampleId: existingSample.id });
      }

      if (!entriesToProcess.length) {
        setStatus(STATUS.finalizing);
        setProgressValue(100);
        setStatus(STATUS.complete);
        onSuccess?.(dataset || existingDataset);
        return;
      }

      const perSampleWeight = UPLOAD_TOTAL_WEIGHT / entriesToProcess.length;
      const datasetId = dataset?.id || existingDataset?.id;
      let activeUploadStartedAt = null;
      let activeUploadSampleName = null;
      let activeUploadSampleBytes = 0;

      const makeUploadEstimateCallbacks = () => ({
        onSampleUploadStart: (entry, sampleBytes) => {
          activeUploadStartedAt = Date.now();
          activeUploadSampleName = entry?.name || null;
          activeUploadSampleBytes = sampleBytes;
          setUploadEstimate({
            startedAt: activeUploadStartedAt,
            uploadElapsedMs: 0,
            totalBytes: sampleBytes,
            uploadedBytes: 0,
            sampleName: activeUploadSampleName,
            sampleBytes
          });
        },
        onSampleUploadProgress: (loaded, total) => {
          const totalBytes = total || 0;
          const uploadedBytes = Math.min(totalBytes, loaded);
          const uploadElapsedMs = activeUploadStartedAt ? Date.now() - activeUploadStartedAt : 0;

          setUploadEstimate({
            startedAt: activeUploadStartedAt,
            uploadElapsedMs,
            totalBytes,
            uploadedBytes,
            sampleName: activeUploadSampleName,
            sampleBytes: activeUploadSampleBytes
          });
        },
        onSampleUploadComplete: () => {
          activeUploadStartedAt = null;
          activeUploadSampleName = null;
          activeUploadSampleBytes = 0;
          setUploadEstimate(null);
        },
        onSampleUploadError: () => {
          activeUploadStartedAt = null;
          activeUploadSampleName = null;
          activeUploadSampleBytes = 0;
          setUploadEstimate(null);
        }
      });

      for (let index = 0; index < entriesToProcess.length; index += 1) {
        const { entry, existingSampleId } = entriesToProcess[index];
        await processSampleEntry({
          entry,
          datasetId,
          perSampleWeight,
          existingSampleId,
          mutations: { createSample, createConcatenatedFastqgz, triggerAnalysis },
          advance,
          setStatus,
          samplePosition: { index, total: entriesToProcess.length },
          uploadEstimate: makeUploadEstimateCallbacks()
        });
      }

      setStatus(STATUS.finalizing);
      setProgressValue(100);
      setStatus(STATUS.complete);
      onSuccess?.(dataset);
    } catch (error) {
      setStatus({ detail: '' });
      setProgress(0);
      setUploadEstimate(null);
      onError?.(error);
    } finally {
      setIsUploading(false);
      setUploadEstimate(null);
    }
  }, [createConcatenatedFastqgz, createSample, createSequencingRun, getAccessTokenSilently, onError, onSuccess, projectId, triggerAnalysis, updateSample, updateSequencingRun]);

  return { upload, progress, status, isUploading, uploadEstimate };
}

export default useMinKNOWUpload;
